/**
 * ModelConfigTable — standalone admin surface for the eight
 * model/provider pairs that drive the pipeline. One row per use
 * case; each row is read-only until clicked, then expands inline
 * with a provider dropdown + model text input + Save / Cancel /
 * Test controls.
 *
 * Design choices:
 *   - ZERO hardcoded model-name strings anywhere. The provider
 *     dropdown has a fixed 4-option taxonomy (anthropic / openai
 *     / google / openrouter) — that taxonomy is stable — but the
 *     model id is always a free-text input. A stale suggestion
 *     list used to bite us every time a new model shipped; a
 *     text box never goes stale.
 *   - Data comes from the existing /admin/config surface
 *     (getSystemConfig + patchSystemConfig). No new endpoint for
 *     read/write.
 *   - Test button calls /admin/models/test with the CURRENT row
 *     values (saved or in-flight edits) so an admin can probe a
 *     new model before committing.
 *
 * Component is self-contained — takes no props. The parent
 * screen just drops <ModelConfigTable /> into place.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, CheckCircle, AlertTriangle, Save, X, Zap,
  Pencil, ExternalLink,
} from 'lucide-react';
import {
  getSystemConfig,
  patchSystemConfig,
  testModel,
  SystemConfigRow,
  TestModelResult,
} from '@/api/client';


import { apiErrorText } from '../../lib/describeApiError';
interface RowDef {
  label: string;
  hint: string;
  modelKey: string;
  providerKey: string;
}

// One entry per pipeline use case. Hardcoded because the mapping
// from human-readable role → config keys is stable; only the VALUES
// of those keys are admin-editable.
const MODEL_ROWS: RowDef[] = [
  {
    label: 'IDF Analysis & Q&A',
    hint: '',
    modelKey: 'qa_model',
    providerKey: 'qa_provider',
  },
  {
    label: 'Novelty, IS & Claim Drafting',
    hint: '',
    modelKey: 'default_draft_model',
    providerKey: 'default_draft_provider',
  },
  {
    label: 'QC Review',
    hint: '',
    modelKey: 'qc_model',
    providerKey: 'qc_provider',
  },
  {
    label: 'Classification & Scoring',
    hint: 'Bucket classification, completeness scoring',
    modelKey: 'default_fast_model',
    providerKey: 'default_fast_provider',
  },
  {
    label: 'Classification Fallback',
    hint: 'Used when primary classification fails',
    modelKey: 'fast_fallback_model',
    providerKey: 'fast_fallback_provider',
  },
  {
    label: 'Document Indexing',
    hint: 'PageIndex tree generation',
    modelKey: 'tree_model',
    providerKey: 'tree_provider',
  },
  {
    label: 'Vision',
    hint: 'Image description for uploaded IDFs',
    modelKey: 'vision_model',
    providerKey: 'vision_provider',
  },
  {
    label: 'Retrieval',
    hint: 'Used by retrieveContext when grounding LLM prompts in IDF excerpts',
    modelKey: 'retrieval_model',
    providerKey: 'retrieval_provider',
  },
  {
    label: 'Provenance',
    hint: 'Generates the per-claim provenance appendix on export',
    modelKey: 'provenance_model',
    providerKey: 'provenance_provider',
  },
];

// 8-option dropdown — the full provider catalogue exposed by the
// backend dispatcher (Phase 13C added nvidia/local/custom on top of
// the original four; Fireworks landed after that). Keep in sync with
// the providers returned from GET /api/admin/models/available.
const PROVIDER_OPTIONS = [
  'anthropic',
  'openai',
  'google',
  'openrouter',
  'fireworks',
  'nvidia',
  'local',
  'custom',
] as const;
type ProviderValue = typeof PROVIDER_OPTIONS[number];

const PROVIDER_LABELS: Record<ProviderValue, string> = {
  anthropic:  'Anthropic',
  openai:     'OpenAI',
  google:     'Google',
  openrouter: 'OpenRouter',
  fireworks:  'Fireworks',
  nvidia:     'NVIDIA Build',
  local:      'Local',
  custom:     'Custom',
};

// Static documentation URLs. Plain <a> tags — never need to be
// updated because the vendor docs pages are stable. nvidia/local/
// custom resolve to admin-configured base_url at runtime; their
// "docs" entries point at the relevant catalogue / setup notes.
const PROVIDER_LINKS: Record<ProviderValue, string> = {
  anthropic:  'https://docs.anthropic.com/models',
  openai:     'https://platform.openai.com/docs/models',
  google:     'https://aistudio.google.com/models',
  openrouter: 'https://openrouter.ai/models',
  fireworks:  'https://fireworks.ai/models',
  nvidia:     'https://build.nvidia.com/models',
  local:      'https://github.com/ollama/ollama#model-library',
  custom:     'https://platform.openai.com/docs/api-reference/chat',
};


function formatProviderLabel(raw: string): string {
  const key = (raw || '').trim().toLowerCase();
  if ((PROVIDER_OPTIONS as readonly string[]).includes(key)) {
    return PROVIDER_LABELS[key as ProviderValue];
  }
  // Blank provider means "auto-infer from model string"
  return raw ? raw : '— auto —';
}

function formatUpdatedAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString();
}


interface RowState {
  // Current saved values from DB.
  provider: string;
  model: string;
  updatedAt: string | null;
  // Edit buffers — only meaningful when this row is expanded.
  draftProvider: string;
  draftModel: string;
}


function RowEditor({
  rowDef,
  state,
  onChange,
  onSave,
  onCancel,
  onTest,
  saving,
  testing,
  testResult,
}: {
  rowDef: RowDef;
  state: RowState;
  onChange: (next: Partial<RowState>) => void;
  onSave: () => void;
  onCancel: () => void;
  onTest: () => void;
  saving: boolean;
  testing: boolean;
  testResult: TestModelResult | null;
}) {
  const providerValue = state.draftProvider || '';
  const providerKnown = (PROVIDER_OPTIONS as readonly string[]).includes(providerValue);
  const docsHref = providerKnown ? PROVIDER_LINKS[providerValue as ProviderValue] : null;
  const canSave = !!state.draftProvider && !!state.draftModel.trim() && !saving;

  return (
    <tr data-testid={`model-row-editor-${rowDef.modelKey}`} className="bg-blue-50/30">
      <td colSpan={5} className="px-4 py-3">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-2">
            <label className="text-xs text-zinc-500 sm:pt-1.5">Provider</label>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid={`model-row-provider-${rowDef.modelKey}`}
                value={providerValue}
                onChange={(e) => onChange({ draftProvider: e.target.value })}
                disabled={saving}
                className="h-8 rounded-sm border border-zinc-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-50"
              >
                {PROVIDER_OPTIONS.map((p) => (
                  <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
                ))}
              </select>
              {docsHref && (
                <a
                  href={docsHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline"
                  title={`Open ${PROVIDER_LABELS[providerValue as ProviderValue]} model catalogue`}
                >
                  {PROVIDER_LABELS[providerValue as ProviderValue]} models
                  <ExternalLink size={10} />
                </a>
              )}
            </div>

            <label className="text-xs text-zinc-500 sm:pt-1.5">Model</label>
            <input
              type="text"
              data-testid={`model-row-model-${rowDef.modelKey}`}
              value={state.draftModel}
              onChange={(e) => onChange({ draftModel: e.target.value })}
              disabled={saving}
              spellCheck={false}
              placeholder="Paste the model id from the provider's documentation"
              className="h-8 rounded-sm border border-zinc-300 bg-white px-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-50"
            />
          </div>

          <div className="flex flex-col items-stretch gap-2 sm:min-w-[220px]">
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`model-row-save-${rowDef.modelKey}`}
                onClick={onSave}
                disabled={!canSave}
                className="h-8 px-3 rounded-sm text-xs font-medium bg-[#002FA7] text-white hover:bg-[#002277] inline-flex items-center gap-1 disabled:opacity-50"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save
              </button>
              <button
                type="button"
                data-testid={`model-row-cancel-${rowDef.modelKey}`}
                onClick={onCancel}
                disabled={saving}
                className="h-8 px-3 rounded-sm text-xs font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <X size={12} />
                Cancel
              </button>
              <button
                type="button"
                data-testid={`model-row-test-${rowDef.modelKey}`}
                onClick={onTest}
                disabled={testing || !state.draftProvider || !state.draftModel.trim()}
                title="Send a one-token test prompt using the values above"
                className="h-8 px-3 rounded-sm text-xs font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 inline-flex items-center gap-1 disabled:opacity-50"
              >
                {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                Test
              </button>
            </div>
            {testResult && <TestResultPill result={testResult} />}
          </div>
        </div>
        {rowDef.hint && (
          <p className="text-[11px] text-zinc-500 mt-2">{rowDef.hint}</p>
        )}
      </td>
    </tr>
  );
}


function TestResultPill({ result }: { result: TestModelResult }) {
  if (result.ok) {
    return (
      <span
        data-testid="model-row-test-result-ok"
        className="inline-flex items-center gap-1 text-[11px] text-green-800 bg-green-50 border border-green-200 rounded-sm px-2 py-0.5"
      >
        <CheckCircle size={11} />
        OK · {Math.round((result.latency_ms || 0))}ms
      </span>
    );
  }
  const statusFragment = result.status ? `${result.status} · ` : '';
  return (
    <span
      data-testid="model-row-test-result-error"
      title={result.error || 'Unknown error'}
      className="inline-flex items-start gap-1 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-sm px-2 py-0.5 max-w-[280px]"
    >
      <AlertTriangle size={11} className="shrink-0 mt-0.5" />
      <span className="truncate">{statusFragment}{result.error || 'failed'}</span>
    </span>
  );
}


/**
 * Props
 * ────
 * onSaved — optional callback fired after a row save succeeds. Lets the
 *   parent Settings screen refetch its own `settings` + `activeModels`
 *   state so the adjacent ACTIVE CONFIGURATION summary doesn't keep
 *   showing the pre-edit model. Without this the parent state is loaded
 *   once on mount and drifts relative to system_config the moment the
 *   admin saves a row here.
 */
export default function ModelConfigTable({
  onSaved,
}: {
  onSaved?: () => void | Promise<void>;
} = {}) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Keyed by modelKey so each row has independent saved + draft state.
  const [rows, setRows] = useState<Record<string, RowState>>({});
  // Which row is currently expanded into edit mode (null = all collapsed).
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  // Transient highlight after a successful save.
  const [flashKey, setFlashKey] = useState<string | null>(null);
  // Per-row test state — one button can be probing at a time.
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestModelResult | null>>({});

  // One-time fetch of every system_config row, then pluck the 16 we
  // care about. Cheap — the full response is <100 rows.
  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await getSystemConfig();
      const flat: Record<string, SystemConfigRow> = {};
      for (const bucket of Object.values(data.categories || {})) {
        for (const row of bucket) flat[row.key] = row;
      }
      const next: Record<string, RowState> = {};
      for (const def of MODEL_ROWS) {
        const modelRow = flat[def.modelKey];
        const providerRow = flat[def.providerKey];
        const provider = (providerRow?.value || '').trim();
        const model = (modelRow?.value || '').trim();
        next[def.modelKey] = {
          provider,
          model,
          updatedAt: modelRow?.updated_at || null,
          draftProvider: provider,
          draftModel: model,
        };
      }
      setRows(next);
    } catch (e: any) {
      setErr(
        e?.response?.status === 403
          ? 'Admin access required to view model configuration.'
          : 'Failed to load model configuration.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-clear test results after 8s so stale green ticks don't stay
  // on screen forever. Per-row timer keyed by modelKey.
  useEffect(() => {
    const entries = Object.entries(testResults).filter(([, v]) => v);
    if (entries.length === 0) return;
    const timers = entries.map(([key]) =>
      setTimeout(() => setTestResults((prev) => ({ ...prev, [key]: null })), 8_000),
    );
    return () => { timers.forEach(clearTimeout); };
  }, [testResults]);

  const startEdit = (modelKey: string) => {
    if (savingKey) return;
    setRows((prev) => {
      const curr = prev[modelKey];
      if (!curr) return prev;
      return {
        ...prev,
        [modelKey]: { ...curr, draftProvider: curr.provider, draftModel: curr.model },
      };
    });
    // Default to "anthropic" when the stored provider is blank so
    // the dropdown has a concrete selection. Admin can still change.
    setRows((prev) => {
      const curr = prev[modelKey];
      if (!curr) return prev;
      if (curr.draftProvider) return prev;
      return { ...prev, [modelKey]: { ...curr, draftProvider: PROVIDER_OPTIONS[0] } };
    });
    setEditingKey(modelKey);
  };

  const cancelEdit = (modelKey: string) => {
    setRows((prev) => {
      const curr = prev[modelKey];
      if (!curr) return prev;
      return {
        ...prev,
        [modelKey]: { ...curr, draftProvider: curr.provider, draftModel: curr.model },
      };
    });
    setEditingKey(null);
  };

  const patchRowState = (modelKey: string, partial: Partial<RowState>) => {
    setRows((prev) => {
      const curr = prev[modelKey];
      if (!curr) return prev;
      return { ...prev, [modelKey]: { ...curr, ...partial } };
    });
  };

  const saveRow = async (def: RowDef) => {
    const current = rows[def.modelKey];
    if (!current) return;
    setSavingKey(def.modelKey);
    setErr(null);
    try {
      // Two system_config writes — one per key. The backend
      // patchSystemConfig handler is idempotent + cache-clearing, so
      // firing them in parallel is safe.
      await Promise.all([
        patchSystemConfig(def.providerKey, current.draftProvider),
        patchSystemConfig(def.modelKey, current.draftModel.trim()),
      ]);
      await load();
      setEditingKey(null);
      setFlashKey(def.modelKey);
      setTimeout(() => setFlashKey((k) => (k === def.modelKey ? null : k)), 1_500);
      // Poke the parent so its ACTIVE CONFIGURATION panel + API key
      // status chips refresh alongside our own rows.
      if (onSaved) {
        try { await onSaved(); } catch { /* parent refresh is best-effort */ }
      }
    } catch (e: any) {
      setErr(apiErrorText(e, 'Save failed'));
    } finally {
      setSavingKey(null);
    }
  };

  const runTest = async (def: RowDef) => {
    const current = rows[def.modelKey];
    if (!current) return;
    const provider = current.draftProvider || current.provider;
    const model = (current.draftModel || current.model).trim();
    if (!provider || !model) return;
    setTestingKey(def.modelKey);
    setTestResults((prev) => ({ ...prev, [def.modelKey]: null }));
    try {
      const res = await testModel(provider, model);
      setTestResults((prev) => ({ ...prev, [def.modelKey]: res }));
    } catch (e: any) {
      setTestResults((prev) => ({
        ...prev,
        [def.modelKey]: {
          ok: false,
          latency_ms: 0,
          error: apiErrorText(e, 'Test request failed'),
        },
      }));
    } finally {
      setTestingKey(null);
    }
  };

  const renderedRows = useMemo(() => MODEL_ROWS, []);

  if (loading) {
    return (
      <div
        data-testid="model-config-loading"
        className="border border-zinc-200 rounded-sm bg-white p-6 text-sm text-zinc-500 inline-flex items-center gap-2"
      >
        <Loader2 size={14} className="animate-spin" /> Loading model configuration…
      </div>
    );
  }

  if (err) {
    return (
      <div
        data-testid="model-config-error"
        className="border border-red-200 rounded-sm bg-red-50 p-4 text-sm text-red-800 inline-flex items-center gap-2"
      >
        <AlertTriangle size={14} /> {err}
      </div>
    );
  }

  return (
    <div
      data-testid="model-config-table"
      className="border border-zinc-200 rounded-sm bg-white overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-zinc-900">Model Configuration</h3>
          <p className="text-[11px] text-zinc-500">
            One row per pipeline use case. Click a row to change its provider and model.
            Model ids are free text — paste whatever the provider's documentation calls it.
          </p>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-zinc-500 text-xs">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Use Case</th>
            <th className="text-left px-4 py-2 font-medium">Provider</th>
            <th className="text-left px-4 py-2 font-medium">Model</th>
            <th className="text-left px-4 py-2 font-medium w-[120px]">Test</th>
            <th className="text-left px-4 py-2 font-medium w-[120px]">Last Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {renderedRows.map((def) => {
            const state = rows[def.modelKey];
            if (!state) return null;
            const isEditing = editingKey === def.modelKey;
            const flashed = flashKey === def.modelKey;
            const lastResult = testResults[def.modelKey] || null;
            return (
              <React.Fragment key={def.modelKey}>
                <tr
                  data-testid={`model-row-${def.modelKey}`}
                  onClick={() => !isEditing && startEdit(def.modelKey)}
                  className={
                    'cursor-pointer transition-colors '
                    + (flashed
                      ? 'bg-green-50'
                      : isEditing
                        ? 'bg-blue-50/40'
                        : 'hover:bg-zinc-50')
                  }
                >
                  <td className="px-4 py-2.5 align-top">
                    <div className="font-medium text-zinc-900">{def.label}</div>
                    {def.hint && (
                      <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">
                        {def.hint}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top text-zinc-700">
                    {formatProviderLabel(state.provider)}
                  </td>
                  <td className="px-4 py-2.5 align-top font-mono text-[12px] text-zinc-700 max-w-[280px] truncate">
                    {state.model || <span className="italic text-zinc-400">— not set —</span>}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        data-testid={`model-row-test-inline-${def.modelKey}`}
                        onClick={(e) => { e.stopPropagation(); runTest(def); }}
                        disabled={testingKey === def.modelKey || !state.provider || !state.model}
                        title={
                          !state.provider || !state.model
                            ? 'Click the row to set provider + model first'
                            : 'Send a one-token test prompt'
                        }
                        className="h-7 px-2 rounded-sm text-[11px] font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        {testingKey === def.modelKey
                          ? <Loader2 size={10} className="animate-spin" />
                          : <Zap size={10} />}
                        Test
                      </button>
                      {!isEditing && lastResult && <TestResultPill result={lastResult} />}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 align-top text-[11px] text-zinc-500">
                    {formatUpdatedAt(state.updatedAt)}
                    {!isEditing && (
                      <button
                        type="button"
                        className="ml-2 text-zinc-400 hover:text-zinc-700"
                        title="Edit row"
                        onClick={(e) => { e.stopPropagation(); startEdit(def.modelKey); }}
                      >
                        <Pencil size={11} />
                      </button>
                    )}
                  </td>
                </tr>
                {isEditing && (
                  <RowEditor
                    rowDef={def}
                    state={state}
                    onChange={(partial) => patchRowState(def.modelKey, partial)}
                    onSave={() => saveRow(def)}
                    onCancel={() => cancelEdit(def.modelKey)}
                    onTest={() => runTest(def)}
                    saving={savingKey === def.modelKey}
                    testing={testingKey === def.modelKey}
                    testResult={lastResult}
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
